/* ============================================================
   URL ↔ module routing for Lens — Phase 2 simplified (2026-04-25)

   After the Phase 2 ID rename (cpa/cma/cfa/fnd → actuals/budgets/
   forecasts/fcc), module IDs equal URL paths. This file is now a
   thin validator + pathname/tab bridge — no translation needed.

   Public API:
     - MODULES                    — canonical module ID list
     - DEFAULT_TAB, DEFAULT_URL
     - pathnameToActiveTab(path)  — URL path → internal "module:section"
     - activeTabToPathname(tab)   — internal tab string → URL path
     - isPlainLeftClick(event)    — true iff plain click (not middle/cmd/ctrl/shift)
   ============================================================ */

export const MODULES = Object.freeze([
  'actuals', 'budgets', 'forecasts', 'support-stack', 'cash-monitor', 'synthesis', 'fcc', 'drivers', 'settings',
]);

const MODULE_SET = new Set(MODULES);

export const DEFAULT_TAB = 'actuals';
export const DEFAULT_URL = '/' + DEFAULT_TAB;

const VALID_SECTIONS_BY_MODULE = Object.freeze({
  actuals: new Set(['assets', 'liabilities', 'equity', 'income', 'primary-income', 'formal', 'comparative']),
  budgets: new Set(['ratios', 'recurring', 'payables', 'ar-streams', 'working-capital', 'formal', 'comparative']),
  forecasts: new Set(['drivers', 'overlays', 'comparison', 'model-assumptions', 'formal', 'comparative']),
  'support-stack': new Set(['sub-ledgers', 'schedules', 'trackers', 'write-intents']),
  'cash-monitor': new Set(['daily-plan', 'receipts', 'obligations', 'plan-variants', 'warnings']),
  synthesis: new Set(['trajectory', 'variance', 'tornado', 'contribution', 'ledger-projection', 'formal', 'comparative']),
  fcc: new Set(['liquid', 'nc-assets-liabilities', 'journal-entries', 'transactions', 'three-stmt', 'comparative']),
  drivers: new Set(['recurring', 'model-drivers', 'piecewise']),
  settings: new Set(['model-assumptions', 'profiles', 'visibility', 'guides', 'presentation', 'snapshot']),
});

export function canonicalizeActiveTab(activeTab) {
  if (!activeTab) return activeTab;
  const [moduleId, sectionId] = String(activeTab).split(':');
  const validSections = VALID_SECTIONS_BY_MODULE[moduleId];
  if (sectionId && validSections && !validSections.has(sectionId)) return moduleId;
  return activeTab;
}

function pathnameToActiveTabRaw(pathname) {
  const clean = String(pathname || '/').replace(/^\/+|\/+$/g, '');
  if (!clean) return DEFAULT_TAB;
  const [moduleId, sectionId] = clean.split('/');
  if (!MODULE_SET.has(moduleId)) return DEFAULT_TAB;
  return sectionId ? `${moduleId}:${sectionId}` : moduleId;
}

export function canonicalizePathname(pathname) {
  return activeTabToPathname(pathnameToActiveTab(pathname));
}

/** Parse a pathname like "/actuals/je-list" into the internal
 *  "actuals:je-list" route string. Unknown modules fall back to DEFAULT_TAB. */
export function pathnameToActiveTab(pathname) {
  return canonicalizeActiveTab(pathnameToActiveTabRaw(pathname));
}

/** Convert an internal "actuals:je-list" route string into a clean URL path. */
export function activeTabToPathname(activeTab) {
  if (!activeTab) return DEFAULT_URL;
  const [moduleId, sectionId] = String(canonicalizeActiveTab(activeTab)).split(':');
  return sectionId ? `/${moduleId}/${sectionId}` : `/${moduleId}`;
}

/** Middle-click / ctrl-click / cmd-click / shift-click should all use native
 *  browser behavior (opens new tab / new window / downloads). Only a plain
 *  left-click (button 0, no modifiers) should be intercepted for client-side
 *  navigation. */
export function isPlainLeftClick(e) {
  if (!e) return false;
  if (e.button !== 0) return false;
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return false;
  return true;
}
